// use-task-subscription.js: // Page view for editing a single task in detail.
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/features/auth/useAuth';
import { buildTaskTree } from '@/features/tasks/utils/structure/buildTaskTree';
import { subscribeToTasks } from '@/services/taskService';

/**
 * Returns a Set of task IDs that share a duplicate `orderKey` with a sibling
 * within the same parent group.
 *
 * Completed tasks (`task.completed === true`) are excluded from the check —
 * only active tasks can produce a conflict.
 *
 * Grouping strategy: tasks are grouped by `task.parentId || 'root'`, so
 * root-level tasks and child tasks are checked independently.
 *
 * @param {Array<Object>} tasks - Flat list of all task objects.
 * @returns {Set<string>} Set of task IDs that have a conflicting orderKey
 *   shared with at least one sibling in the same parent group.
 */
/** @public */
export const findDuplicateOrderKeyTaskIds = (tasks) => {
  const conflictingTaskIds = new Set();
  const activeTasks = tasks.filter((task) => !task.completed);

  const groupedByParent = activeTasks.reduce((acc, task) => {
    const parentId = task.parentId || 'root';
    if (!acc[parentId]) {
      acc[parentId] = [];
    }
    acc[parentId].push(task);
    return acc;
  }, {});

  for (const parentId in groupedByParent) {
    const children = groupedByParent[parentId];
    const seenOrderKeys = {};

    for (const child of children) {
      if (seenOrderKeys[child.orderKey]) {
        seenOrderKeys[child.orderKey].push(child);
      } else {
        seenOrderKeys[child.orderKey] = [child];
      }
    }

    for (const orderKey in seenOrderKeys) {
      if (seenOrderKeys[orderKey].length > 1) {
        const conflictingTasks = seenOrderKeys[orderKey];
        conflictingTasks.forEach((task) => conflictingTaskIds.add(task.id));
      }
    }
  }
  return conflictingTaskIds;
};

/**
 * React hook that subscribes to the current user's non-archived tasks in
 * Firestore and exposes them as reactive state.
 *
 * **Incremental-update strategy**: Instead of rebuilding the full task array
 * on every snapshot, the hook uses Firestore's `docChanges()` to process only
 * the documents that were actually added, modified, or removed. Changes are
 * applied to a `Map` keyed by task ID, so unchanged tasks are never
 * re-processed.
 *
 * **Sort order**: After each update the resulting array is sorted
 * lexicographically on `materializedPath`, which guarantees that tasks appear
 * in hierarchical (depth-first, parent-before-child) order.
 *
 * @warning **Performance Limitation (V3-TASK-008)**:
 * The sorting on `materializedPath` is currently done **client-side** in memory.
 * For extremely large and deep task trees (e.g., thousands of active tasks),
 * this `Array.prototype.sort()` operation runs on every snapshot update and
 * could become a UI bottleneck.
 *
 * **Future Scalability Path**: If the user backlog grows too large, this should be
 * refactored to server-side sorting (e.g., passing `orderBy("materializedPath")`
 * directly to the Firestore query). Note that doing so requires a composite index
 * in Firestore (`userId` ASC, `archived` ASC, `materializedPath` ASC).
 *
 * @returns {{
 *   allTasks: import('@/types').Task[],
 *   taskTree: any[],
 *   loading: boolean,
 *   error: Error|null,
 *   duplicateOrderKeyTaskIds: Set<string>
 * }}
 */
const useTaskSubscription = () => {
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [duplicateOrderKeyTaskIds, setDuplicateOrderKeyTaskIds] = useState(
    new Set(),
  );
  const { user } = useAuth();
  const prevTasksRef = useRef([]);
  const loggedDuplicateIdsRef = useRef(new Set());

  useEffect(() => {
    if (!user) {
      setAllTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToTasks(
      user.uid,
      (processedChanges) => {
        setAllTasks((prevTasks) => {
          const taskMap = new Map(prevTasks.map((task) => [task.id, task]));

          processedChanges.forEach((change) => {
            if (change.type === 'removed') {
              taskMap.delete(change.id);
            } else {
              taskMap.set(change.task.id, change.task);
            }
          });

          const updatedTasks = Array.from(taskMap.values()).map((task) => {
            if (!task.id) {
              const generated = crypto.randomUUID();
              return { ...task, id: generated };
            }
            return task;
          });

          updatedTasks.sort((a, b) => {
            const keyA = a.materializedPath || '';
            const keyB = b.materializedPath || '';

            if (keyA < keyB) return -1;
            if (keyA > keyB) return 1;
            return 0;
          });

          return updatedTasks;
        });
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user]);

  // Recalculate duplicates when the task list changes.
  useEffect(() => {
    const duplicatesSet = findDuplicateOrderKeyTaskIds(allTasks);

    const newlyDetected = [...duplicatesSet].filter(
      (id) => !loggedDuplicateIdsRef.current.has(id),
    );
    if (newlyDetected.length > 0) {
      const prevById = new Map(
        prevTasksRef.current.map((task) => [task.id, task]),
      );
      console.warn(
        'duplicate orderKey detected',
        newlyDetected.map((id) => {
          const task = allTasks.find((t) => t.id === id);
          const prevTask = prevById.get(id);
          return {
            id,
            title: task?.title,
            parentId: task?.parentId ?? null,
            orderKey: task?.orderKey,
            prevOrderKey: prevTask?.orderKey,
            materializedPath: task?.materializedPath,
          };
        }),
      );
    }
    loggedDuplicateIdsRef.current = duplicatesSet;
    prevTasksRef.current = allTasks;

    setDuplicateOrderKeyTaskIds(duplicatesSet);
  }, [allTasks]);

  const taskTree = useMemo(() => buildTaskTree(allTasks), [allTasks]);

  return { allTasks, taskTree, loading, error, duplicateOrderKeyTaskIds };
};

export default useTaskSubscription;
